const ACCEPTED_EXT = ['.jpg', '.jpeg', '.png', '.webp', '.gif', '.svg'];
const LEAD_THUMB_WIDTH = 1600;
const GALLERY_THUMB_WIDTH = 1200;

function isUsefulImageTitle(title: string): boolean {
  const t = String(title || '').toLowerCase();
  // Wikimedia file namespace is localized (e.g. "Datei:" on de.wikipedia)
  if (!/^(file|datei):/.test(t)) return false;
  if (!ACCEPTED_EXT.some((ext) => t.endsWith(ext))) return false;
  return !(
    t.includes('icon') ||
    t.includes('logo') ||
    t.includes('map') ||
    t.includes('locator') ||
    t.includes('coat of arms')
  );
}

async function fetchLeadImage(title: string, wikiDomain: string, signal: AbortSignal): Promise<string | null> {
  const params = new URLSearchParams({
    action: 'query',
    titles: title,
    prop: 'pageimages',
    piprop: 'thumbnail|original',
    pithumbsize: String(LEAD_THUMB_WIDTH),
    format: 'json',
    origin: '*',
  });

  const resp = await fetch(`https://${wikiDomain}/w/api.php?${params.toString()}`, { signal });
  if (!resp.ok) return null;
  const data = await resp.json();
  const pages = data.query?.pages || {};
  const pageId = Object.keys(pages)[0];
  return pages?.[pageId]?.thumbnail?.source || pages?.[pageId]?.original?.source || null;
}

async function fetchImageUrls(
  imageTitles: string[],
  wikiDomain: string,
  signal: AbortSignal,
): Promise<string[]> {
  if (!imageTitles.length) return [];

  const params = new URLSearchParams({
    action: 'query',
    titles: imageTitles.join('|'),
    prop: 'imageinfo',
    iiprop: 'url',
    iiurlwidth: String(GALLERY_THUMB_WIDTH),
    format: 'json',
    origin: '*',
  });

  const resp = await fetch(`https://${wikiDomain}/w/api.php?${params.toString()}`, { signal });
  if (!resp.ok) return [];

  const data = await resp.json();
  const pages = data.query?.pages || {};
  return Object.values(pages)
    .map((p: any) => p?.imageinfo?.[0]?.thumburl || p?.imageinfo?.[0]?.url)
    .filter((u: any) => !!u);
}

async function fetchArticleImages(title: string, wikiDomain: string, signal: AbortSignal): Promise<string[]> {
  const listParams = new URLSearchParams({
    action: 'query',
    titles: title,
    prop: 'images',
    imlimit: '25',
    format: 'json',
    origin: '*',
  });

  const resp = await fetch(`https://${wikiDomain}/w/api.php?${listParams.toString()}`, { signal });
  if (!resp.ok) return [];

  const data = await resp.json();
  const pages = data.query?.pages || {};
  const pageId = Object.keys(pages)[0];
  const images = pages?.[pageId]?.images || [];

  const filteredTitles = images
    .map((img: any) => img?.title)
    .filter((t: any) => typeof t === 'string' && isUsefulImageTitle(t))
    .slice(0, 20);

  return fetchImageUrls(filteredTitles, wikiDomain, signal);
}

export async function fetchWikipediaArticleImages(params: {
  title: string;
  wikiLang: string;
  signal: AbortSignal;
}): Promise<string[]> {
  const { title, wikiLang, signal } = params;
  const wikiDomain = `${wikiLang || 'en'}.wikipedia.org`;

  try {
    const [lead, rest] = await Promise.all([
      fetchLeadImage(title, wikiDomain, signal),
      fetchArticleImages(title, wikiDomain, signal),
    ]);

    const out: string[] = [];
    if (lead) out.push(lead);
    rest.forEach((u) => {
      if (!out.includes(u)) out.push(u);
    });

    return out;
  } catch {
    return [];
  }
}
